import { get, push, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { deleteFolderAndUnlinkContents } from "./folderDeletion";
import { CONTENT_FOLDER_ROOT_ID } from "../utils/constants";
import {
  isDuplicateFolderName,
  normalizeFolderName,
  normalizeParentFolderID,
} from "../utils/contentFolderUtils";
import { releaseOwnListener } from "../utils/releaseOwnListener";

const getContentFoldersPath = ({ advertiserID }) =>
  `contentFolders/${advertiserID}`;

const getContentFolderPath = ({ advertiserID, folderID }) =>
  `${getContentFoldersPath({ advertiserID })}/${folderID}`;

const getContentFolderChildrenPath = ({ advertiserID, parentFolderID }) =>
  `contentFolderChildren/${advertiserID}/${parentFolderID}`;

const getContentFolderContentIndexPath = ({
  advertiserID,
  folderID,
  contentID,
}) => `contentFolderContents/${advertiserID}/${folderID}/${contentID}`;

export const startListenerContentFolders =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getContentFoldersPath({ advertiserID }));
      const unsubscribe = onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });
      onReferenceAvailable(unsubscribe);

      return unsubscribe;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerContentFolders",
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerContentFolders =
  ({ unsubscribe } = {}) =>
  () => {
    releaseOwnListener(unsubscribe);
  };

export const startCreateContentFolder =
  ({ name, parentFolderID = CONTENT_FOLDER_ROOT_ID }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);
    const normalizedParentID = normalizeParentFolderID(parentFolderID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const foldersPath = getContentFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: normalizedParentID,
          name: normalizedName,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const folderID = push(ref(db, foldersPath)).key;
      const now = Date.now();
      const folder = {
        name: normalizedName,
        parentFolderID: normalizedParentID,
        creationTime: now,
        updatedTime: now,
      };

      await update(ref(db), {
        [getContentFolderPath({ advertiserID, folderID })]: folder,
        [`${getContentFolderChildrenPath({
          advertiserID,
          parentFolderID: normalizedParentID,
        })}/${folderID}`]: true,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          ...folder,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateContentFolder",
          context: { name: normalizedName, parentFolderID: normalizedParentID },
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startMoveContentsToFolder =
  ({ contentIDs, folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || !folderID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const targetFolderSnapshot = await get(
        ref(db, getContentFolderPath({ advertiserID, folderID }))
      );
      if (!targetFolderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(db, `contentList/${advertiserID}/${contentID}`)
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const movedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content) return;

        const previousFolderID = content.folderID;
        if (previousFolderID === folderID) return;

        if (previousFolderID) {
          updates[
            getContentFolderContentIndexPath({
              advertiserID,
              folderID: previousFolderID,
              contentID,
            })
          ] = null;
        }

        updates[
          getContentFolderContentIndexPath({
            advertiserID,
            folderID,
            contentID,
          })
        ] = true;
        updates[`contentList/${advertiserID}/${contentID}/folderID`] = folderID;
        movedContentIDs.push(contentID);
      });

      if (movedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: movedContentIDs,
          folderID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMoveContentsToFolder",
          context: { contentIDs, folderID },
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUnassignContentsFromFolder =
  ({ contentIDs }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(db, `contentList/${advertiserID}/${contentID}`)
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const unassignedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content?.folderID) return;

        updates[
          getContentFolderContentIndexPath({
            advertiserID,
            folderID: content.folderID,
            contentID,
          })
        ] = null;
        updates[`contentList/${advertiserID}/${contentID}/folderID`] = null;
        unassignedContentIDs.push(contentID);
      });

      if (unassignedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: unassignedContentIDs,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUnassignContentsFromFolder",
          context: { contentIDs },
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRenameContentFolder =
  ({ folderID, name }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const folderSnapshot = await get(
        ref(db, getContentFolderPath({ advertiserID, folderID }))
      );

      if (!folderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const currentFolder = folderSnapshot.val();
      const foldersPath = getContentFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: currentFolder.parentFolderID,
          name: normalizedName,
          excludeFolderID: folderID,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const now = Date.now();
      await update(ref(db), {
        [`${getContentFolderPath({ advertiserID, folderID })}/name`]:
          normalizedName,
        [`${getContentFolderPath({ advertiserID, folderID })}/updatedTime`]:
          now,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          name: normalizedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameContentFolder",
          context: { folderID, name: normalizedName },
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteContentFolder =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      return await deleteFolderAndUnlinkContents({
        advertiserID,
        folderID,
        foldersRoot: "contentFolders",
        childrenRoot: "contentFolderChildren",
        contentsRoot: "contentFolderContents",
        getEntityPaths: (id) => [`contentList/${advertiserID}/${id}`],
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteContentFolder",
          context: { folderID },
          tags: { action: "contentFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
